import { createContext, useCallback, useMemo } from "react";

// products
import actionTypes from "../../common/V2/actionTypes";
// constants
import { DATABASE_STATUTES, DEFAULT_PRODUCT_FILTER_VALUES } from "../../common/constants"; // helpers
import { productReducer } from "../../reducers/V2";

import axios from "../../utils/axiosV2";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";

// product constants
const LIST_PRODUCTS = "/v2/products";
const CSV_UPLOAD_URL = "/v2/products/csv-upload";
const CSV_UPLOADS_URL = "/v2/products/csv-uploads";

const initialState = {
  products: [],
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  csvUploads: [],
  csvUploadsPagination: {},
  statuses: {
    loadProducts: DATABASE_STATUTES.NOT_LOADED,
    loadCsvUploads: DATABASE_STATUTES.NOT_LOADED,
    uploadCsv: DATABASE_STATUTES.NOT_LOADED,
  },
  loading: false,
  error: null,
  operationState: {
    isProcessing: false,
    progress: 0,
    type: null,
    message: "",
  },
};

const ProductsContext = createContext({
  ...initialState,
  fetchProducts: () => {},
  clearSearch: () => {},
  getProductById: () => Promise.resolve(null),
  uploadCsv: () => Promise.resolve(null),
  fetchCsvUploads: () => Promise.resolve(null),
});

const ProductsProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(productReducer, initialState);
  // Fetch products that are in Timesact.
  const fetchProducts = useCallback(
    async ({ page = 1, limit = 25, search = null, sortBy = null, sortOrder = "ASC", ...otherFilters } = {}) => {
      dispatch({ type: actionTypes.FETCH_PRODUCTS });

      try {
        // Map customerExperience to customerExperienceId for backend
        const { customerExperience, ...restFilters } = otherFilters;
        const params = {
          page,
          limit,
          ...(search && { search }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
          ...(customerExperience && { customerExperienceId: customerExperience }),
          ...restFilters,
        };

        const response = await axios.get(LIST_PRODUCTS, { params });

        if (response.data && response.data.success) {
          dispatch({
            type: actionTypes.FETCH_PRODUCTS_SUCCESS,
            payload: {
              products: response.data.data.products || [],
              pagination: response.data.pagination || {},
            },
          });
        } else {
          throw new Error(response.data.error || "Failed to fetch products");
        }
      } catch (error) {
        console.error("fetchProducts error:", error);
        dispatch({
          type: actionTypes.FETCH_PRODUCTS_FAILURE,
          payload: error.message,
        });
      }
    },
    [dispatch]
  );

  const getProductById = useCallback(async (productId) => {
    try {
      const response = await axios.get(`${LIST_PRODUCTS}/${productId}`);
      if (response.data && response.data.success) {
        return response.data.data.product;
      }
      throw new Error(response.data?.error || "Failed to get product");
    } catch (error) {
      console.error("getProductById error:", error);
      throw error;
    }
  }, []);

  const clearSearch = useCallback(() => {
    dispatch({
      type: actionTypes.CLEAR_SEARCH,
    });
  }, [dispatch]);

  // Operation management functions
  const startOperation = useCallback(
    (type, message) => {
      dispatch({
        type: actionTypes.START_OPERATION,
        payload: { type, message },
      });
    },
    [dispatch]
  );

  const updateOperationProgress = useCallback(
    (progress) => {
      dispatch({
        type: actionTypes.UPDATE_OPERATION_PROGRESS,
        payload: { progress },
      });
    },
    [dispatch]
  );

  const completeOperation = useCallback(() => {
    dispatch({
      type: actionTypes.COMPLETE_OPERATION,
    });
  }, [dispatch]);

  const createTimesactProducts = useCallback(
    async ({ type, data, customerExperiences = [] }) => {
      console.log("createTimesactProducts called with:", { type, data, customerExperiences });

      dispatch({ type: actionTypes.FETCH_PRODUCTS }); // Set loading state

      try {
        if (type === "products" && data?.products) {
          // Handle individual products
          const productsToCreate = data.products.map((product) => {
            // Extract integer ID from Shopify GID (e.g., "gid://shopify/Product/10248990392657" -> "10248990392657")
            const extractId = (gid) => (gid ? gid.split("/").pop() : null);

            // Transform product data to match API schema
            const transformedProduct = {
              type: "MANUAL", // Set the correct type as required by schema
              shopifyProductId: extractId(product.id),
              title: product.title,
              handle: product.handle,
              img: product.images?.[0]?.originalSrc || null,
              vendor: product.vendor || null,
              tags: product.tags || [],
              variants:
                product.variants?.map((variant) => {
                  return {
                    shopifyProductVariantId: extractId(variant.id),
                    title: variant.title,
                    sku: variant.sku,
                    price: parseFloat(variant.price) || null,
                    shopifyQuantity: variant.inventoryQuantity || 0,
                    displayName: variant.displayName,
                    inventoryPolicy: variant.inventoryPolicy || "DENY",
                    shopifyInventoryItemId: extractId(variant.inventoryItem?.id) || "",
                    shopifyUpdatedAt: variant.updatedAt || new Date().toISOString(),
                    shopifyDeletedAt: null,
                  };
                }) || [],
              customerExperiences: customerExperiences.map((exp) => ({
                id: exp.id,
                startDate: exp.startDate,
                endDate: exp.endDate,
                preOrderQuantity: exp.preorderQuantity,
              })),
            };

            return transformedProduct;
          });

          // Create products one by one with progress tracking
          let completedProducts = 0;
          const totalProducts = productsToCreate.length;

          for (const product of productsToCreate) {
            try {
              const response = await axios.post(LIST_PRODUCTS, product);
              completedProducts++;

              // Update progress
              updateOperationProgress((completedProducts / totalProducts) * 100);
            } catch (error) {
              console.error(`Error creating product ${product.shopifyProductId}:`, error);
              completedProducts++;

              // Update progress even on error
              updateOperationProgress((completedProducts / totalProducts) * 100);
            }
          }

          // Refresh the products list
          await fetchProducts();

          // Complete operation
          completeOperation();

          return { success: true };
        }

        throw new Error(`Unsupported type: ${type}`);
      } catch (error) {
        console.error("createTimesactProducts error:", error);
        dispatch({
          type: actionTypes.FETCH_PRODUCTS_FAILURE,
          payload: error.message,
        });
        throw error;
      }
    },
    [dispatch, fetchProducts, updateOperationProgress, completeOperation]
  );

  const deleteProductVariants = useCallback(
    async (productGroups) => {
      console.log("deleteProductVariants called with:", { productGroups });

      try {
        const productEntries = Object.entries(productGroups);
        let completedProducts = 0;
        const totalProducts = productEntries.length;

        // Process each product and its variants
        for (const [productId, variantIds] of productEntries) {
          try {
            const response = await axios.delete(`${LIST_PRODUCTS}/${productId}`, {
              data: { variantIds },
            });

            // Handle 204 No Content response (successful delete)
            if (response.status === 204) {
              // Remove from local state
              dispatch({
                type: actionTypes.DELETE_PRODUCT_SUCCESS,
                payload: { productId, variantIds },
              });
            }

            completedProducts++;

            // Update progress
            updateOperationProgress((completedProducts / totalProducts) * 100);
          } catch (error) {
            console.error(`Error deleting product ${productId}:`, error);
            completedProducts++;

            // Update progress even on error
            updateOperationProgress((completedProducts / totalProducts) * 100);
          }
        }

        // Refresh the products list
        await fetchProducts();

        // Complete operation
        completeOperation();

        return { success: true };
      } catch (error) {
        console.error("deleteProductVariants error:", error);
        dispatch({
          type: actionTypes.DELETE_PRODUCT_FAILURE,
          payload: error.message,
        });
        throw error;
      }
    },
    [dispatch, updateOperationProgress, completeOperation, fetchProducts]
  );

  const updateTimesactProducts = useCallback(
    async ({ productGroups, customerExperiences }) => {
      console.log("updateTimesactProducts called with:", { productGroups, customerExperiences });

      dispatch({ type: actionTypes.FETCH_PRODUCTS }); // Set loading state

      try {
        const productEntries = Object.entries(productGroups);
        let completedProducts = 0;
        const totalProducts = productEntries.length;

        // Process each product and its variants
        for (const [productId, variantIds] of productEntries) {
          try {
            const response = await axios.put(`${LIST_PRODUCTS}/${productId}/variants`, {
              variantIds: variantIds,
              customerExperiences: customerExperiences.map((exp) => ({
                id: exp.id,
                startDate: exp.startDate,
                endDate: exp.endDate,
                preOrderQuantity: exp.preorderQuantity,
              })),
            });

            completedProducts++;

            // Update progress
            updateOperationProgress((completedProducts / totalProducts) * 100);
          } catch (error) {
            console.error(`Error updating product ${productId}:`, error);
            completedProducts++;

            // Update progress even on error
            updateOperationProgress((completedProducts / totalProducts) * 100);
          }
        }

        // Refresh the products list
        await fetchProducts();

        // Complete operation
        completeOperation();

        return { success: true };
      } catch (error) {
        console.error("updateTimesactProducts error:", error);
        dispatch({
          type: actionTypes.FETCH_PRODUCTS_FAILURE,
          payload: error.message,
        });
        throw error;
      }
    },
    [dispatch, fetchProducts, updateOperationProgress, completeOperation]
  );

  const changeVariantCustomerExperience = useCallback(
    async (productId, variantId, fromCustomerExperienceId, toCustomerExperienceId) => {
      console.log("changeVariantCustomerExperience called with:", {
        productId,
        variantId,
        fromCustomerExperienceId,
        toCustomerExperienceId,
      });

      try {
        const response = await axios.put(`${LIST_PRODUCTS}/${productId}/variants/${variantId}/customer-experiences`, {
          fromCustomerExperienceId: String(fromCustomerExperienceId),
          toCustomerExperienceId: String(toCustomerExperienceId),
        });

        if (response.data && response.data.success) {
          const { data } = response.data;

          // Update the local state with the new variant customer experience
          console.log("Dispatching UPDATE_VARIANT_CUSTOMER_EXPERIENCE_SUCCESS with:", {
            productId: data.productId,
            variantId: data.variantId,
            variantCustomerExperience: data.variantCustomerExperience,
          });
          dispatch({
            type: actionTypes.UPDATE_VARIANT_CUSTOMER_EXPERIENCE_SUCCESS,
            payload: {
              productId: data.productId,
              variantId: data.variantId,
              variantCustomerExperience: data.variantCustomerExperience,
            },
          });

          return {
            success: true,
            data,
            message: "Customer experience updated successfully",
          };
        } else {
          throw new Error(response.data.error || "Failed to update customer experience");
        }
      } catch (error) {
        console.error("changeVariantCustomerExperience error:", error);

        dispatch({
          type: actionTypes.UPDATE_VARIANT_CUSTOMER_EXPERIENCE_FAILURE,
          payload: error.response?.data?.message || error.message || "Failed to update customer experience",
        });

        throw error;
      }
    },
    [dispatch]
  );

  const uploadCsv = useCallback(
    async (file) => {
      dispatch({ type: actionTypes.UPLOAD_CSV });
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(CSV_UPLOAD_URL, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data?.success) {
          dispatch({
            type: actionTypes.UPLOAD_CSV_SUCCESS,
            payload: response.data.data.upload,
          });
          return response.data.data.upload;
        }
        throw new Error(response.data?.message || "Failed to upload CSV");
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        const errors = error.response?.data?.errors || [];
        dispatch({ type: actionTypes.UPLOAD_CSV_FAILURE, payload: message });
        throw { message, errors };
      }
    },
    [dispatch]
  );

  const fetchCsvUploads = useCallback(
    async ({ page = 1, limit = 25 } = {}) => {
      dispatch({ type: actionTypes.FETCH_CSV_UPLOADS });
      try {
        const response = await axios.get(CSV_UPLOADS_URL, { params: { page, limit } });

        if (response.data?.success) {
          dispatch({
            type: actionTypes.FETCH_CSV_UPLOADS_SUCCESS,
            payload: {
              uploads: response.data.data.uploads || [],
              pagination: response.data.pagination || {},
            },
          });
          return response.data.data.uploads;
        }
        throw new Error(response.data?.message || "Failed to fetch CSV uploads");
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_CSV_UPLOADS_FAILURE,
          payload: error.message,
        });
      }
    },
    [dispatch]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      dispatch,
      fetchProducts,
      clearSearch,
      getProductById,
      createTimesactProducts,
      deleteProductVariants,
      updateTimesactProducts,
      changeVariantCustomerExperience,
      startOperation,
      updateOperationProgress,
      completeOperation,
      uploadCsv,
      fetchCsvUploads,
    }),
    [
      state,
      dispatch,
      fetchProducts,
      clearSearch,
      getProductById,
      createTimesactProducts,
      deleteProductVariants,
      updateTimesactProducts,
      changeVariantCustomerExperience,
      startOperation,
      updateOperationProgress,
      completeOperation,
      uploadCsv,
      fetchCsvUploads,
    ]
  );

  return <ProductsContext.Provider value={memoizedState}>{children}</ProductsContext.Provider>;
};

export { ProductsContext, ProductsProvider };
